/**
 * The agenda round: each side proposes questions, the visitor picks.
 *
 * Free's design. Before any debating happens, both agents are asked to propose
 * two or three questions about the topic. The visitor chooses at least one, and
 * those become the debate's structure.
 *
 * This round earns its keep three times over:
 *
 * 1. It is a SMOKE TEST. If a model cannot produce a coherent question it is not
 *    going to produce a coherent argument, and we learn that in one cheap
 *    generation rather than six expensive ones.
 * 2. It is a LATTICE FITNESS PROBE. Vague questions from one side mean that side
 *    has little purchase on the topic. That was the failure measured on
 *    2026-07-26: the Gardener retrieved at 0.60 on a sowing question while the
 *    Hermeticist retrieved at 0.46 and ended up paraphrasing. Here a lopsided
 *    pairing shows itself in the agenda, before the debate is spent.
 * 3. It sets the AGENDA, which is what makes the opening statements independent.
 *    Both sides answer the same question rather than answering each other, and a
 *    speaker with nothing to echo cannot echo.
 *
 * Parsing lives here, dependency-free, because a model's list formatting is
 * exactly the kind of thing that needs a dozen cases pinned down by tests
 * rather than eyeballed once.
 */

import { CONFIDENT_RECALL } from "./toolParse.ts";
import { prompt as renderPrompt, type PromptOverrides } from "./promptKit.ts";

/** A question, and which side proposed it. Attribution is shown to the visitor. */
export type ProposedQuestion = {
  text: string;
  /** Pack key of the agent that proposed it. */
  fromKey: string;
  fromPersona: string;
};

/** Lines that are the model talking ABOUT the task rather than doing it. */
const PREAMBLE =
  /^(here|these|below|okay|ok|sure|certainly|as requested|i (will|have|can)|my |the following|proposed questions|questions?:)/i;

const INTERROGATIVE = /^(how|what|why|when|where|which|who|whom|whose|should|could|would|can|do|does|did|is|are|was|were|will|has|have|had|if)\b/i;

/**
 * Pull questions out of a model's free-form list.
 *
 * Deliberately strict about what counts. A false question ("Here are three
 * questions about soil:") would be offered to the visitor as something to debate
 * and would waste a whole round, so anything that does not look like an actual
 * question is dropped rather than guessed at.
 */
/**
 * Longest thing still treated as one question.
 *
 * WAS 240, AND THAT SILENTLY BROKE THE FITNESS PROBE. Free spotted it in the
 * lab: the Hermeticist visibly proposed three questions and the panel reported
 * "1 proposed". Its second and third ran 253 and 266 characters, so both were
 * dropped for LENGTH, and neither was malformed:
 *
 *   "Considering the established symbolic language used in the *Book of the
 *    Secret of Creation*, does the concept of 'resonance' hold significance
 *    regarding interspecies communication..."
 *
 * The lost questions were the smaller half of the damage. The question COUNT is
 * read as a lattice-fitness signal, on the reasoning that a side which cannot
 * frame a question will not argue well, so a formatting cap was being reported
 * as a memory that had nothing to say. Any earlier conclusion drawn from a low
 * proposal count is suspect for the same reason, including the 2026-07-26 note
 * that the Hermeticist offered one question on a sowing topic.
 *
 * 600 is a sanity bound against a whole paragraph arriving as one line, not a
 * style preference. A verbose model asked for a debate question legitimately
 * writes long ones.
 */
export const MAX_QUESTION_CHARS = 600;
export const MIN_QUESTION_CHARS = 12;

/** Why a line was rejected. Surfaced so a cap can never again be invisible. */
export type DroppedLine = { text: string; why: string };

export type AgendaParse = { questions: string[]; dropped: DroppedLine[] };

/**
 * The parse, with its rejects.
 *
 * NO SILENT CAPS. Everything this refuses is reported, because the failure it
 * just caused was invisible by construction: the count looked like a fact about
 * the lattice and was a fact about the parser.
 */
export function parseAgenda(raw: string, max = 3): AgendaParse {
  const out: string[] = [];
  const dropped: DroppedLine[] = [];
  const seen = new Set<string>();

  for (const line of raw.split(/\r?\n|(?<=\?)\s+(?=[A-Z])/)) {
    let t = line.trim();
    if (!t) continue;

    // Strip list markers and bold: "1.", "2)", "-", "*", "**3.**", ">".
    //
    // `question 1:` is here because a model wrote it, not because anyone
    // predicted it. Asked for "one question per line, nothing else", the
    // Hermeticist numbered its lines in WORDS, and half the visitor's list
    // arrived reading "question 1: What specific physiological changes...".
    // Anchored and bounded on purpose: a line that genuinely begins "Question 3
    // of the Corpus Hermeticum is..." has no colon there and is left alone.
    t = t
      .replace(/^>+\s*/, "")
      .replace(/^\*\*\s*/, "")
      .replace(/^[-*•]\s+/, "")
      .replace(/^\(?\d+[.)]\s*/, "")
      .replace(/^(?:question|q)\s*\d*\s*[:.)-]\s*/i, "")
      .replace(/^\*\*/, "")
      .replace(/\*\*$/, "")
      .replace(/^["“']|["”']$/g, "")
      .trim();

    if (t.length < MIN_QUESTION_CHARS) continue; // fragments, not a rejection worth reporting
    if (t.length > MAX_QUESTION_CHARS) {
      dropped.push({ text: t, why: `${t.length} chars, over ${MAX_QUESTION_CHARS}` });
      continue;
    }
    // A question mark anywhere, or an interrogative opening. Both, because
    // small models drop the mark surprisingly often.
    const looksLikeQuestion = t.includes("?") || INTERROGATIVE.test(t);
    if (!looksLikeQuestion) {
      dropped.push({ text: t, why: "no question mark and no interrogative opening" });
      continue;
    }
    // Preamble can still end in a colon and contain "questions", so it is
    // checked AFTER marker stripping and only rejected when it is not itself
    // phrased as a question.
    if (!t.includes("?") && PREAMBLE.test(t)) {
      dropped.push({ text: t, why: "reads as preamble" });
      continue;
    }

    const key = t.toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();
    if (seen.has(key)) {
      dropped.push({ text: t, why: "duplicate" });
      continue;
    }
    seen.add(key);
    out.push(t);
    if (out.length >= max) break;
  }
  return { questions: out, dropped };
}

/** The list alone, for callers that do not care why anything was refused. */
export function parseProposedQuestions(raw: string, max = 3): string[] {
  return parseAgenda(raw, max).questions;
}

/**
 * The prompt that asks for questions.
 *
 * Asks for questions the agent could argue about FROM ITS OWN KNOWLEDGE, which
 * is what makes the round a fitness probe rather than a creativity test. An
 * agent with nothing on the subject will produce generalities, and that is the
 * signal we want.
 */
export function agendaPrompt(
  otherPersona: string,
  topic: string,
  n = 3,
  ov?: PromptOverrides,
): string {
  return renderPrompt("agenda", { other: otherPersona, topic, n: String(n) }, ov);
}

/**
 * Merge both sides' proposals, keeping attribution and dropping near-duplicates.
 *
 * Interleaved rather than concatenated, so the list a visitor reads alternates
 * between the two framings instead of front-loading one side. Whose questions
 * the debate runs on shapes the debate, and a visitor skimming a list tends to
 * pick from the top.
 */
export function mergeAgenda(
  a: { key: string; persona: string; questions: string[] },
  b: { key: string; persona: string; questions: string[] },
  max = 6,
): ProposedQuestion[] {
  const out: ProposedQuestion[] = [];
  const seen = new Set<string>();
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();

  const rounds = Math.max(a.questions.length, b.questions.length);
  for (let i = 0; i < rounds && out.length < max; i++) {
    for (const side of [a, b]) {
      const q = side.questions[i];
      if (!q || out.length >= max) continue;
      const k = norm(q);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ text: q, fromKey: side.key, fromPersona: side.persona });
    }
  }
  return out;
}

/**
 * Did this side pass its smoke test?
 *
 * Separate from "did it produce parseable lines", because the interesting
 * failure is a model that produces well-formed questions with nothing behind
 * them. Retrieval strength is the tell, and it is the caller's to supply.
 */
export type AgendaHealth = {
  ok: boolean;
  questions: number;
  /** Best retrieval score the agent's own lattice offered on the topic. */
  topScore: number | null;
  /** Set when something is worth telling the visitor before they commit. */
  warning?: string;
  /**
   * Lines the parser refused, and why.
   *
   * Carried into the health because the question count is read as a signal
   * about the LATTICE, and a parser that quietly discards well-formed questions
   * makes that signal a lie. It did exactly that: a 240-character cap dropped
   * two good questions and the panel reported the side as nearly mute.
   */
  dropped?: DroppedLine[];
};

/**
 * Below this, warn the visitor that this side has little to say on the topic.
 *
 * Now an ALIAS rather than its own number. The comment here used to read "the
 * floor the aperture itself uses; below it a fact is not offered at all", and
 * that was simply wrong: the aperture's floor is 0.45 and this is 0.5, so there
 * is a band where facts ARE offered and this still warns. That band is exactly
 * where the 2026-07-27 banner failure lived, and a comment asserting the two
 * numbers were the same thing is part of why it went unseen.
 *
 * "Its memory has little to say on this" and "do not bind the answer to this"
 * are one judgement, so they are one constant.
 */
export const WEAK_LATTICE_SCORE = CONFIDENT_RECALL;

export function agendaHealth(
  questions: string[],
  topScore: number | null,
  dropped: DroppedLine[] = [],
): AgendaHealth {
  const base = { topScore, dropped: dropped.length > 0 ? dropped : undefined };

  if (questions.length === 0) {
    return {
      ...base,
      ok: false,
      questions: 0,
      // Distinguish "said nothing" from "said things this parser refused".
      // Reporting the second as the first is what turned a formatting cap into
      // a false verdict on a lattice.
      warning:
        dropped.length > 0
          ? `It proposed ${dropped.length} line${dropped.length === 1 ? "" : "s"} and none survived parsing, ` +
            `so this is a parser result rather than a memory result.`
          : "This one could not propose a question about the topic.",
    };
  }
  if (topScore !== null && topScore < WEAK_LATTICE_SCORE) {
    return {
      ...base,
      ok: true,
      questions: questions.length,
      warning:
        "Its memory has little to say on this subject, so expect it to agree " +
        "rather than argue. A topic closer to what it knows will debate better.",
    };
  }
  return { ...base, ok: true, questions: questions.length };
}
